import type { ComponentProps } from 'react';

import { Box, Heading, VStack } from '@chakra-ui/react';
import Editor from '@monaco-editor/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useColorMode } from '#src/components/ui/color-mode';

type MonacoStandaloneEditor = Parameters<
  NonNullable<ComponentProps<typeof Editor>['onMount']>
>[0];

function normalizeToData(value: unknown): unknown {
  if (value === undefined) return null;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as unknown;
    } catch {
      return value;
    }
  }
  return value;
}

/**
 * Pretty view for audit JSON: real newlines inside string values are shown as separate lines
 * (not as \\n in one mega-line). Long single-line strings are left for Monaco to soft-wrap.
 * Output is JSON-like but not always valid JSON when strings are expanded.
 */
function formatJsonReadable(value: unknown, indent: number): string {
  const pad = '  '.repeat(indent);
  const pad1 = '  '.repeat(indent + 1);

  if (value === null) return 'null';
  if (typeof value === 'boolean' || typeof value === 'number') {
    return JSON.stringify(value);
  }
  if (typeof value === 'string') {
    if (!value.includes('\n')) return JSON.stringify(value);
    const lines = value.split('\n');
    return `\n${pad1}${lines.join(`\n${pad1}`)}`;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    const parts = value.map((item, i) => {
      const formatted = formatJsonReadable(item, indent + 1);
      const body = formatted.startsWith('\n') ? formatted : ` ${formatted}`;
      return `${pad1}[${i}]:${body}`;
    });
    return `[\n${parts.join(',\n')}\n${pad}]`;
  }
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    const keys = Object.keys(o);
    if (keys.length === 0) return '{}';
    const parts = keys.map((k) => {
      const v = o[k];
      const formatted = formatJsonReadable(v, indent + 1);
      const key = JSON.stringify(k);
      if (formatted.startsWith('\n')) {
        return `${pad1}${key}:${formatted}`;
      }
      return `${pad1}${key}: ${formatted}`;
    });
    return `{\n${parts.join(',\n')}\n${pad}}`;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function jsonToEditorText(value: unknown): string {
  const data = normalizeToData(value);
  try {
    return formatJsonReadable(data, 0);
  } catch {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  }
}

interface JsonReadonlyBlockProps {
  title: string;
  value: unknown;
  /** Used when `fitContentHeight` is false. */
  height?: string;
  /**
   * When true, editor height follows content (wrapped lines included). Ignores `height` for the
   * Monaco viewport; clamps between `minContentHeightPx` and `maxContentHeightPx`.
   */
  fitContentHeight?: boolean;
  minContentHeightPx?: number;
  maxContentHeightPx?: number;
}

const DEFAULT_FIXED_HEIGHT = 'min(50vh, 480px)';
const DEFAULT_MIN_CONTENT_PX = 96;
const DEFAULT_MAX_CONTENT_PX = 1_000_000;

export function JsonReadonlyBlock({
  title,
  value,
  height = DEFAULT_FIXED_HEIGHT,
  fitContentHeight = false,
  minContentHeightPx = DEFAULT_MIN_CONTENT_PX,
  maxContentHeightPx = DEFAULT_MAX_CONTENT_PX,
}: JsonReadonlyBlockProps) {
  const { colorMode } = useColorMode();
  const monacoTheme = colorMode === 'dark' ? 'vs-dark' : 'vs-light';
  const text = useMemo(() => jsonToEditorText(value), [value]);
  const editorRef = useRef<MonacoStandaloneEditor | null>(null);
  const fitContentHeightRef = useRef(fitContentHeight);
  fitContentHeightRef.current = fitContentHeight;
  const [fitHeightPx, setFitHeightPx] = useState(minContentHeightPx);

  const clampContentHeight = useMemo(
    () => (raw: number) =>
      Math.max(minContentHeightPx, Math.min(raw, maxContentHeightPx)),
    [minContentHeightPx, maxContentHeightPx],
  );

  useEffect(() => {
    const ed = editorRef.current;
    if (!ed || !fitContentHeight) return;
    ed.layout();
    setFitHeightPx(clampContentHeight(ed.getContentHeight()));
  }, [fitContentHeight, text, clampContentHeight]);

  const editorHeight = fitContentHeight ? `${fitHeightPx}px` : height;

  return (
    <VStack gap={2} align="stretch">
      <Heading size="sm" color="fg">
        {title}
      </Heading>
      <Box
        borderWidth="1px"
        borderRadius="md"
        borderColor="border"
        overflow="hidden"
      >
        <Editor
          height={editorHeight}
          defaultLanguage="plaintext"
          theme={monacoTheme}
          value={text}
          onMount={(editor) => {
            editorRef.current = editor;
            editor.onDidContentSizeChange(() => {
              if (!fitContentHeightRef.current) return;
              setFitHeightPx(clampContentHeight(editor.getContentHeight()));
            });
          }}
          options={{
            readOnly: true,
            minimap: { enabled: false },
            wordWrap: 'on',
            wrappingStrategy: 'advanced',
            stickyScroll: { enabled: false },
            scrollBeyondLastLine: false,
            folding: true,
            lineNumbers: 'on',
            renderLineHighlight: 'none',
            // Read-only audit JSON: wheel should scroll the page, not Monaco’s viewport.
            scrollbar: {
              handleMouseWheel: false,
              alwaysConsumeMouseWheel: false,
            },
          }}
        />
      </Box>
    </VStack>
  );
}
